import React from 'react';
import { Typography } from '@material-ui/core/';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';

/**
 * The `curve` result format — N labelled series over a shared X axis.
 *
 * Deliberately one view for what used to be several rendered PNGs. An elbow plot, a silhouette plot, a ROC
 * curve, a precision-recall curve and a learning curve are all this shape, so an algorithm that emits it gets
 * a chart without anybody writing a view for it.
 *
 *     curves: [ { title, xLabel, yLabel, series: [ { label, points: [[x, y], …] } ] } ]
 *
 * `points` also accepts `[{ x, y }]`, because that is the shape a pandas `to_dict` tends to produce and
 * rejecting it would be a pointless trap.
 */

const asPoint = (p) => {
    if (Array.isArray(p)) return [Number(p[0]), Number(p[1])];
    if (p && typeof p === 'object') return [Number(p.x), Number(p.y)];
    return null;
};

// A curve with a null in it should show a gap, not connect straight through it or refuse to draw.
const finite = (v) => (typeof v === 'number' && isFinite(v) ? v : null);

export default function CurveChart({ data }) {
    const curves = Array.isArray(data && data.curves) ? data.curves : [];
    if (!curves.length) {
        return <Typography variant="body2" style={{ padding: 20, opacity: 0.7 }}>
            This result has no <code>curves</code> block.
        </Typography>;
    }

    return <div>
        {curves.map((curve, i) => {
            const series = (Array.isArray(curve.series) ? curve.series : []).map((s) => ({
                name: s.label || s.name || 'series',
                data: (Array.isArray(s.points) ? s.points : [])
                    .map(asPoint)
                    .filter(Boolean)
                    .map(([x, y]) => [finite(x), finite(y)])
                    .filter(([x]) => x !== null),
                marker: { enabled: true, radius: 3 }
            }));

            const options = {
                chart: { type: 'line', height: 420, zoomType: 'xy' },
                title: { text: curve.title || `Curve ${i + 1}` },
                xAxis: { title: { text: curve.xLabel || '' }, allowDecimals: true },
                yAxis: { title: { text: curve.yLabel || '' } },
                credits: { enabled: false },
                // One series needs no legend; several do.
                legend: { enabled: series.length > 1 },
                tooltip: { shared: true, valueDecimals: 4 },
                series
            };
            return <div key={i} style={{ marginBottom: 20 }}>
                <HighchartsReact options={options} highcharts={Highcharts} />
                {curve.note
                    ? <Typography variant="caption" style={{ opacity: 0.75 }}>{curve.note}</Typography>
                    : null}
            </div>;
        })}
    </div>;
}
